import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { propertiesApi, settingsApi } from '@/lib/api';
import { defaultAnalysisData } from '@/lib/scoring';
import { useAuth } from '@/lib/auth';
import AnalysisWizard from '@/components/AnalysisWizard';
import ScorePanel from '@/components/ScorePanel';
import { toast } from 'sonner';

export default function AdminAnalysis() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { extendSession } = useAuth();
  const [initialData, setInitialData] = useState(null);
  const [wizardData, setWizardData] = useState(defaultAnalysisData);
  const [settings, setSettings] = useState(null);
  const [thresholds, setThresholds] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [propertyId, setPropertyId] = useState(id || null);
  const [analysisId, setAnalysisId] = useState(null);
  const autoSaveTimer = useRef(null);
  const lastSavedData = useRef(null);
  const isEdit = !!id;

  useEffect(() => {
    // Load settings for fee calculation
    settingsApi.get()
      .then(res => setSettings(res.data))
      .catch(() => {});
    settingsApi.getThresholds()
      .then(res => setThresholds(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) { setWizardData(defaultAnalysisData); return; }
    propertiesApi.get(id)
      .then(res => {
        setInitialData(res.data);
        setWizardData(res.data);
        setPropertyId(res.data.id);
        setAnalysisId(res.data.analysis_id);
      })
      .catch(() => toast.error('Objekt nicht gefunden'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleDataChange = useCallback((data) => {
    setWizardData(data);
  }, []);

  // Auto-save functionality
  const handleAutoSave = useCallback(async (data) => {
    const dataStr = JSON.stringify(data);
    if (dataStr === lastSavedData.current) return;
    
    try {
      if (propertyId) {
        await propertiesApi.update(propertyId, data);
        lastSavedData.current = dataStr;
      } else {
        const res = await propertiesApi.create(data);
        lastSavedData.current = dataStr;
        setPropertyId(res.data.id);
        setAnalysisId(res.data.analysis_id);
        // Update URL without full navigation
        window.history.replaceState(null, '', `/admin/analyse/${res.data.id}`);
      }
    } catch (err) {
      console.error('Auto-save error', err);
    }
  }, [propertyId]);

  const handleSubmit = async (data) => {
    setSaving(true);
    try {
      if (propertyId) {
        await propertiesApi.update(propertyId, data);
        toast.success('Analyse aktualisiert');
      } else {
        const res = await propertiesApi.create(data);
        toast.success('Analyse erstellt');
        setPropertyId(res.data.id);
        setAnalysisId(res.data.analysis_id);
        navigate(`/admin/analyse/${res.data.id}`);
      }
      extendSession();
    } catch {
      toast.error('Fehler beim Speichern');
    }
    setSaving(false);
  };

  const handleFeeOverride = useCallback((fee) => {
    const updated = { ...wizardData, custom_fee_override: fee };
    setWizardData(updated);
    // Auto-save the override
    if (propertyId) {
      handleAutoSave(updated);
    }
  }, [wizardData, propertyId, handleAutoSave]);

  const handleDownloadPdf = async (propId) => {
    toast.loading('PDF wird erstellt...', { duration: 5000 });
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const token = localStorage.getItem('token');
    window.open(`${API_URL}/api/properties/${propId}/pdf?token=${token}`, '_blank');
  };

  const handleDownloadOfferPdf = async (propId) => {
    toast.loading('Vorabangebot wird erstellt...', { duration: 5000 });
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const token = localStorage.getItem('token');
    window.open(`${API_URL}/api/properties/${propId}/pdf/offer?token=${token}`, '_blank');
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">Laden...</div>;

  return (
    <div className="animate-fade-in" data-testid="admin-analysis-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">{isEdit ? 'Analyse bearbeiten' : 'Neue Analyse'}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {isEdit ? `Bearbeiten Sie die Analyse für "${initialData?.name || 'WEG'}"` : 'Erstellen Sie eine neue WEG-Analyse'}
        </p>
      </div>
      <div className="flex gap-8">
        <div className="flex-1 min-w-0">
          <AnalysisWizard
            initialData={isEdit ? initialData : undefined}
            onSubmit={handleSubmit}
            onDataChange={handleDataChange}
            onAutoSave={handleAutoSave}
            analysisId={analysisId}
            isAdmin={true}
            isEdit={isEdit}
            loading={saving}
            refurbishmentThresholds={thresholds}
            refurbishmentThresholdsList={settings?.refurbishment_thresholds_list}
            repairItems={settings?.repair_items}
            costEstimates={settings?.refurbishment_cost_estimates}
            modernizationItems={settings?.modernization_items}
            equipmentItems={settings?.equipment_items}
          />
        </div>
        <div className="hidden lg:block w-[22rem] xl:w-[26rem] shrink-0">
          <div className="sticky top-6">
            <ScorePanel 
              data={wizardData} 
              settings={settings} 
              analysisId={analysisId}
              propertyId={propertyId}
              onDownloadPdf={handleDownloadPdf}
              onDownloadOfferPdf={handleDownloadOfferPdf}
              onFeeOverride={handleFeeOverride}
              isAdmin={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
